import React from "react";
import PropTypes from "prop-types";
import TextInput from "../common/TextInput";
import SelectInput from "../common/SelectInput";
import Dropdown from "../common/Dropdown";
import RadioButtonGroup from "../common/RadioButtonGroup";
import Row from "../common/Row";
import * as DomainConstants from "../../constants/DomainConstants";
import RepeatingRow from "../common/RepeatingRow";

const AanvragerForm = ({
	aanvrager,
	aanvragerIndex,
	disabled,
	errors,
	juridischeStatuten,
	postcodes,
	gemeenten,
	onChange,
	onAddGemandateerde,
	onRemoveGemandateerde,
	onChangeGemandateerde,
	isHighlighted,
	validationMessageType
}) => {

	const BTWAFTREKBAAR_TOOLTIP = "BTW die je zelf moet betalen, kan je in mindering brengen van de BTW die je van jouw klanten hebt ontvangen. Dat noemt men de BTW-aftrek.";
	const GEMANDATEERDE_TOOLTIP = "Dit is de gevolmachtigde persoon met betrekking tot de ondertekening van het aanvraagformulier. De gemandateerde(n) verschijnt op het einde ook automatisch onderaan het aanvraagformulier (PDF) als ondertekenaar(s).";
	const ONDERNEMINGSNR_TOOLTIP = "Je ondernemingsnummer - toegekend bij je inschrijving in de Kruispuntbank van Ondernemingen (KBO) - bestaat uit een 0 of een 1, gevolgd door negen cijfers.";

	const jaNee = [
		{ value: "true", label: 'Ja' },
		{ value: "false", label: 'Nee' }
	];

	const sharedProps = {
		disabled,
		onChange
	};

	const isAnderJuridischStatuut = aanvrager.juridischeStatuut == DomainConstants.ANDER_JURIDISH_STATUUT_GUID;
	let extrafieldSize = isAnderJuridischStatuut ? 4 : 6;

	const anderJuridischStatuutComponent = isAnderJuridischStatuut ? (<TextInput
		label="Ander juridisch statuut (max. 200 tekens)"
		name="anderJuridischStatuut"
		value={aanvrager.anderJuridischStatuut}
		{...sharedProps}
		size={4}
		validationMessage={errors ? errors.anderJuridischStatuut : ''}
		maxLength={200}
		validationMessageType={validationMessageType}
	/>) : null;

	function mapRemoveGemandateerde(i) {
		return function (event) {
			onRemoveGemandateerde(event, i, aanvragerIndex);
		};
	}

	function mapAddGemandateerde() {
		return function (event) {
			onAddGemandateerde(event, aanvragerIndex);
		};
	}

	function mapChangeGemandateerde(i) {
		return function (event, data) {
			onChangeGemandateerde(event, i, aanvragerIndex, data);
		};
	}

	const mandatarisRows = aanvrager.mandatarissen.map((mandataris, i) => {
		let mandatarisError = null;
		if (errors && errors.mandatarissen != null) {
			mandatarisError = errors.mandatarissen[i];
		}
		const repeatingRow = (
			<RepeatingRow
				key={i}
				onAddClick={mapAddGemandateerde()}
				onRemoveClick={mapRemoveGemandateerde(i)}
				isDisabled={disabled}>
				<TextInput
					disabled={disabled}
					size={4}
					tooltip={GEMANDATEERDE_TOOLTIP}
					label="Voornaam gemandateerde"
					name="voornaam"
					value={aanvrager.mandatarissen[i].voornaam}
					validationMessage={mandatarisError != null ? mandatarisError.voornaam : ''}
					onChange={mapChangeGemandateerde(i)}
					maxLength={100}
					breakLineIfTooLong
					validationMessageType={validationMessageType} />
				<TextInput
					disabled={disabled}
					size={4}
					label="Naam gemandateerde"
					name="naam"
					value={aanvrager.mandatarissen[i].naam}
					validationMessage={mandatarisError != null ? mandatarisError.naam : ''}
					onChange={mapChangeGemandateerde(i)}
					maxLength={100}
					breakLineIfTooLong
					validationMessageType={validationMessageType} />
				<TextInput
					disabled={disabled}
					size={4}
					label="Functie gemandateerde"
					name="functie"
					value={aanvrager.mandatarissen[i].functie}
					validationMessage={mandatarisError != null ? mandatarisError.functie : ''}
					onChange={mapChangeGemandateerde(i)}
					maxLength={100}
					breakLineIfTooLong
					validationMessageType={validationMessageType} />
			</RepeatingRow>
		);

		return (repeatingRow);
	});

	return (
		<div title="Identificatie van de aanvrager(s)">
			<Row>
				<TextInput
					{...sharedProps}
					size={6}
					label="Naam"
					name="naam"
					value={aanvrager.naam}
					validationMessage={errors ? errors.naam : ''}
					isHighlighted={isHighlighted}
					maxLength={100}
					validationMessageType={validationMessageType} />
				<TextInput
					{...sharedProps}
					size={6}
					label="Ondernemingsnummer"
					name="ondernemingsNr"
					value={aanvrager.ondernemingsNr}
					tooltip={ONDERNEMINGSNR_TOOLTIP}
					validationMessage={errors ? errors.ondernemingsNr : ''}
					isHighlighted={isHighlighted}
					validationMessageType={validationMessageType} />
			</Row>
			<Row>
				<TextInput
					{...sharedProps}
					label="Straat en nummer"
					name="straat"
					value={aanvrager.straat}
					validationMessage={errors ? errors.straat : ''}
					isHighlighted={isHighlighted}
					maxLength={250}
					validationMessageType={validationMessageType} />
			</Row>
			<Row>
				<Dropdown
					{...sharedProps}
					key={aanvrager.accountId + '_postcode'}
					size={3}
					label="Postcode"
					name="postcode"
					value={aanvrager.postcode}
					validationMessage={errors ? errors.postcode : ''}
					items={postcodes}
					noResultsMessage="Tik (2 cijfers) en zoek"
					isHighlighted={isHighlighted}
					validationMessageType={validationMessageType} />
				<Dropdown
					{...sharedProps}
					key={aanvrager.accountId + '_gemeente'}
					size={9}
					label="Gemeente"
					name="plaats"
					value={aanvrager.plaats}
					validationMessage={errors ? errors.plaats : ''}
					items={gemeenten}
					isHighlighted={isHighlighted}
					validationMessageType={validationMessageType} />
			</Row>
			<Row>
				<SelectInput
					{...sharedProps}
					label="Juridisch statuut"
					name="juridischeStatuut"
					value={aanvrager.juridischeStatuut.toString()}
					size={extrafieldSize}
					items={juridischeStatuten}
					validationMessage={errors ? errors.juridischeStatuut : ''}
					isHighlighted={isHighlighted}
					validationMessageType={validationMessageType} />
				{anderJuridischStatuutComponent}
				<RadioButtonGroup
					{...sharedProps}
					label="Is de BTW voor de werken aftrekbaar?"
					name={"btwAftrekbaar" + aanvrager.accountId}
					labelInside
					value={aanvrager.btwAftrekbaar}
					size={extrafieldSize}
					items={jaNee}
					horizontal
					tooltip={BTWAFTREKBAAR_TOOLTIP}
					validationMessage={errors ? errors.btwAftrekbaar : ''}
					isHighlighted={isHighlighted}
					validationMessageType={validationMessageType} />
			</Row>

			{mandatarisRows}

		</div>
	);
};

// ----------------------------------------------------------------------------
AanvragerForm.propTypes = {
	aanvrager: PropTypes.object.isRequired,
	aanvragerIndex: PropTypes.number.isRequired,
	disabled: PropTypes.bool,
	errors: PropTypes.object,
	juridischeStatuten: PropTypes.array,
	postcodes: PropTypes.array.isRequired,
	gemeenten: PropTypes.array.isRequired,
	onChange: PropTypes.func.isRequired,
	onAddGemandateerde: PropTypes.func.isRequired,
	onRemoveGemandateerde: PropTypes.func.isRequired,
	onChangeGemandateerde: PropTypes.func.isRequired,
	isHighlighted: PropTypes.bool,
	validationMessageType: PropTypes.string
};

AanvragerForm.defaultProps = {
	onAddGemandateerde: function () { },
	isHighlighted: false
};

export default AanvragerForm;
